import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import type { Terminal } from '@xterm/xterm';

export const TERMINAL_TOUCH_SELECTION_DELAY_MS = 550;
// Android can report several CSS pixels of finger drift while the user is
// deliberately holding still. Keep this above the browser's usual touch slop;
// the scroll hook still wins immediately once it has moved a whole row.
export const TERMINAL_TOUCH_SELECTION_MOVE_TOLERANCE_PX = 16;
// Mobile browsers may synthesize a trusted mousedown/mouseup/click sequence
// after touchend. xterm focuses its hidden textarea from mousedown, which can
// reopen the IME immediately after a completed selection. Keep the suppression
// short and local to the terminal surface so the next deliberate tap still
// restores normal keyboard focus.
export const TERMINAL_TOUCH_COMPATIBILITY_MOUSE_SUPPRESSION_MS = 800;

export type TerminalTouchGestureOwner = 'idle' | 'pending-selection' | 'selection' | 'scroll';

export type TerminalTouchGestureRef = {
  current: TerminalTouchGestureOwner;
};

export type TerminalTouchSelectionOptions = {
  enabled: boolean;
  gestureOwnerRef?: TerminalTouchGestureRef;
  /** Starts selection on touch-down after the user explicitly arms Select. */
  immediate?: boolean;
  onContextMenuChange?: (visible: boolean) => void;
  onImmediateSelectionComplete?: (active: boolean) => void;
};

type TouchPoint = {
  clientX: number;
  clientY: number;
};

function dispatchSelectionMouseEvent(
  target: EventTarget,
  type: 'mousedown' | 'mousemove' | 'mouseup',
  point: TouchPoint,
  forceSelection: boolean
) {
  target.dispatchEvent(new MouseEvent(type, {
    bubbles: true,
    cancelable: true,
    button: 0,
    buttons: type === 'mouseup' ? 0 : 1,
    clientX: point.clientX,
    clientY: point.clientY,
    detail: type === 'mousedown' ? 2 : 0,
    shiftKey: forceSelection,
    view: window
  }));
}

/**
 * xterm's selection flow focuses its textarea from both the generic mousedown
 * handler and its Linux-selection compatibility path. Android identifies as
 * Linux to xterm (`navigator.platform`), so a synthetic event used for a
 * fullscreen long press would otherwise reopen the native IME. Suppress only
 * synchronous textarea focus during the adapted mouse event; the existing
 * selection service still receives its normal mousedown/move/up sequence.
 */
function dispatchSelectionMouseEventWithoutFocusing(
  terminal: Terminal,
  target: EventTarget,
  type: 'mousedown' | 'mousemove' | 'mouseup',
  point: TouchPoint,
  forceSelection: boolean
) {
  const textarea = terminal.textarea;
  const originalFocus = textarea?.focus;
  if (textarea) textarea.focus = () => undefined;
  try {
    dispatchSelectionMouseEvent(target, type, point, forceSelection);
  } finally {
    if (textarea && originalFocus) textarea.focus = originalFocus;
    // A browser default action or xterm listener may have focused the helper
    // without going through the temporarily replaced method. Selection never
    // needs IME focus, so enforce the postcondition after every adapted event.
    textarea?.blur();
  }
}

/**
 * Adapts a stationary touch followed by a drag to xterm's existing selection
 * service, in both the embedded panel and the maximized terminal. Ordinary
 * movement before the hold threshold remains owned by the touch-scroll hook,
 * keeping the two gestures mutually exclusive while both are enabled. A
 * synthetic Shift-drag is used only when the terminal application has enabled
 * mouse reporting, matching xterm's desktop forced-selection convention.
 */
export function useTerminalTouchSelection<T extends HTMLElement>(
  hostRef: RefObject<T | null>,
  terminalRef: RefObject<Terminal | null>,
  {
    enabled,
    gestureOwnerRef,
    immediate = false,
    onContextMenuChange,
    onImmediateSelectionComplete
  }: TerminalTouchSelectionOptions
) {
  // Selection gestures span several browser events. Keep changing UI state
  // (the context menu and explicit Select toggle) out of the listener
  // lifecycle so a render cannot tear down xterm's active drag between its
  // synthetic mousedown and mousemove.
  const optionsRef = useRef({ immediate, onContextMenuChange, onImmediateSelectionComplete });
  useLayoutEffect(() => {
    optionsRef.current = { immediate, onContextMenuChange, onImmediateSelectionComplete };
  }, [immediate, onContextMenuChange, onImmediateSelectionComplete]);

  useEffect(() => {
    if (!enabled) return;
    const host = hostRef.current;
    if (!host) return;
    const gestureOwner: TerminalTouchGestureRef = gestureOwnerRef ?? { current: 'idle' };

    let startPoint: TouchPoint | null = null;
    let lastPoint: TouchPoint | null = null;
    let selectionTimer: number | null = null;
    let selecting = false;
    let forceSelection = false;
    let startedImmediately = false;
    let suppressCompatibilityMouseUntil = 0;
    let restoreTerminalFocus: (() => void) | null = null;
    let focusRestoreTimer: number | null = null;

    const suppressCompatibilityMouse = () => {
      suppressCompatibilityMouseUntil = window.performance.now() +
        TERMINAL_TOUCH_COMPATIBILITY_MOUSE_SUPPRESSION_MS;
    };
    const releaseTerminalFocusBlock = () => {
      if (focusRestoreTimer !== null) {
        window.clearTimeout(focusRestoreTimer);
        focusRestoreTimer = null;
      }
      restoreTerminalFocus?.();
      restoreTerminalFocus = null;
    };
    const blockTerminalFocus = (terminal: Terminal) => {
      releaseTerminalFocusBlock();
      const textarea = terminal.textarea;
      if (!textarea) return;
      const ownFocusDescriptor = Object.getOwnPropertyDescriptor(textarea, 'focus');
      const blockedFocus = () => undefined;
      textarea.focus = blockedFocus;
      textarea.blur();
      restoreTerminalFocus = () => {
        // Do not overwrite a newer owner that deliberately replaced focus.
        if (textarea.focus !== blockedFocus) return;
        // xterm's Linux selection notification can be delivered after the
        // adapted mouse event. Ensure any such delayed focus is dismissed
        // before restoring the textarea's real focus method.
        textarea.blur();
        if (ownFocusDescriptor) {
          Object.defineProperty(textarea, 'focus', ownFocusDescriptor);
        } else {
          Reflect.deleteProperty(textarea, 'focus');
        }
      };
    };
    const scheduleTerminalFocusRestore = () => {
      if (!restoreTerminalFocus) return;
      if (focusRestoreTimer !== null) window.clearTimeout(focusRestoreTimer);
      focusRestoreTimer = window.setTimeout(
        releaseTerminalFocusBlock,
        TERMINAL_TOUCH_COMPATIBILITY_MOUSE_SUPPRESSION_MS
      );
    };

    const clearTimer = () => {
      if (selectionTimer !== null) {
        window.clearTimeout(selectionTimer);
        selectionTimer = null;
      }
    };
    const reset = () => {
      clearTimer();
      startPoint = null;
      lastPoint = null;
      selecting = false;
      forceSelection = false;
      gestureOwner.current = 'idle';
    };
    // xterm installs document-level mouse listeners and a drag-scroll timer
    // after its synthetic mousedown. Always pair it with a mouseup, including
    // when iOS/Android cancels a touch because the app was backgrounded or a
    // second finger joined the gesture. Merely clearing this hook's state
    // would leave xterm believing a mouse button was still held.
    const finishSelection = (point = lastPoint) => {
      const terminal = terminalRef.current;
      const completedSelection = selecting;
      const completedImmediateSelection = selecting && startedImmediately;
      if (selecting && point && terminal) {
        dispatchSelectionMouseEventWithoutFocusing(
          terminal,
          host.ownerDocument,
          'mouseup',
          point,
          forceSelection
        );
      }
      reset();
      startedImmediately = false;
      if (completedImmediateSelection) optionsRef.current.onImmediateSelectionComplete?.(false);
      if (completedSelection) scheduleTerminalFocusRestore();
    };
    const cancelPendingSelection = () => {
      clearTimer();
      startPoint = null;
    };
    const pointFromTouch = (touch: Touch): TouchPoint => ({
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    const beginSelection = () => {
      selectionTimer = null;
      const terminal = terminalRef.current;
      const xterm = host.querySelector<HTMLElement>('.xterm');
      if (
        gestureOwner.current !== 'pending-selection' ||
        !terminal ||
        !xterm ||
        !startPoint
      ) return;

      optionsRef.current.onContextMenuChange?.(true);
      selecting = true;
      gestureOwner.current = 'selection';
      forceSelection = xterm.classList.contains('enable-mouse-events');
      // Selection is a transcript interaction, not a terminal-input request.
      // Close an already-open IME before the drag starts and suppress the
      // compatibility mouse sequence Android may emit after touchend.
      blockTerminalFocus(terminal);
      terminal.blur();
      suppressCompatibilityMouse();
      terminal.clearSelection();
      dispatchSelectionMouseEventWithoutFocusing(
        terminal,
        xterm,
        'mousedown',
        startPoint,
        forceSelection
      );
      navigator.vibrate?.(10);
    };
    const onTouchStart = (event: TouchEvent) => {
      // A fresh touch is a new user intent. Any compatibility mouse sequence
      // from the prior gesture has already been delivered (or superseded), so
      // it must not suppress this gesture's later deliberate tap.
      releaseTerminalFocusBlock();
      suppressCompatibilityMouseUntil = 0;
      optionsRef.current.onContextMenuChange?.(false);
      if (event.touches.length !== 1) {
        finishSelection();
        return;
      }
      // A new single-finger gesture must first release a prior selection drag
      // if a browser delivered a new touchstart without its matching end.
      finishSelection();
      terminalRef.current?.clearSelection();
      startPoint = pointFromTouch(event.touches[0]);
      lastPoint = startPoint;
      gestureOwner.current = 'pending-selection';
      clearTimer();
      if (optionsRef.current.immediate) {
        startedImmediately = true;
        beginSelection();
      } else {
        selectionTimer = window.setTimeout(beginSelection, TERMINAL_TOUCH_SELECTION_DELAY_MS);
      }
    };
    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        finishSelection();
        return;
      }
      const nextPoint = pointFromTouch(event.touches[0]);
      lastPoint = nextPoint;

      if (selecting && gestureOwner.current === 'selection') {
        event.preventDefault();
        event.stopImmediatePropagation();
        const terminal = terminalRef.current;
        if (!terminal) return;
        dispatchSelectionMouseEventWithoutFocusing(
          terminal,
          host.ownerDocument,
          'mousemove',
          nextPoint,
          forceSelection
        );
        return;
      }

      if (gestureOwner.current === 'scroll') {
        cancelPendingSelection();
        return;
      }

      if (
        startPoint &&
        Math.hypot(
          nextPoint.clientX - startPoint.clientX,
          nextPoint.clientY - startPoint.clientY
        ) > TERMINAL_TOUCH_SELECTION_MOVE_TOLERANCE_PX
      ) {
        gestureOwner.current = 'scroll';
        cancelPendingSelection();
      }
    };
    const onTouchEnd = (event: TouchEvent) => {
      clearTimer();
      if (selecting) {
        event.preventDefault();
        event.stopImmediatePropagation();
        suppressCompatibilityMouse();
        const endPoint = event.changedTouches[0]
          ? pointFromTouch(event.changedTouches[0])
          : lastPoint;
        finishSelection(endPoint);
        return;
      }
      reset();
    };
    const onContextMenu = (event: Event) => {
      // The xterm renderer is not native selectable DOM text. Avoid showing an
      // empty browser menu during the hold gesture; an actual selection gets
      // contextual copy and paste actions from the panel.
      if (selectionTimer !== null || selecting) {
        event.preventDefault();
        // Some Android WebViews deliver their native hold signal before a
        // throttled JavaScript timer. Treat it as the same long press instead
        // of merely suppressing the otherwise-empty browser menu.
        if (!selecting) {
          clearTimer();
          beginSelection();
        }
      }
    };
    const onVisibilityChange = () => {
      if (host.ownerDocument.visibilityState !== 'visible') {
        optionsRef.current.onContextMenuChange?.(false);
        finishSelection();
      }
    };
    const onWindowBlur = () => {
      optionsRef.current.onContextMenuChange?.(false);
      finishSelection();
    };
    const onCompatibilityMouse = (event: MouseEvent) => {
      // The synthetic events used to drive xterm's selection service are
      // intentionally untrusted and must continue through this capture hook.
      if (
        !event.isTrusted ||
        window.performance.now() > suppressCompatibilityMouseUntil
      ) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    const capture = true;
    host.addEventListener('touchstart', onTouchStart, { capture, passive: true });
    host.addEventListener('touchmove', onTouchMove, { capture, passive: false });
    host.addEventListener('touchend', onTouchEnd, { capture, passive: false });
    host.addEventListener('touchcancel', onTouchEnd, { capture, passive: false });
    host.addEventListener('contextmenu', onContextMenu, capture);
    host.addEventListener('mousedown', onCompatibilityMouse, capture);
    host.addEventListener('mouseup', onCompatibilityMouse, capture);
    host.addEventListener('click', onCompatibilityMouse, capture);
    window.addEventListener('blur', onWindowBlur);
    host.ownerDocument.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      host.removeEventListener('touchstart', onTouchStart, capture);
      host.removeEventListener('touchmove', onTouchMove, capture);
      host.removeEventListener('touchend', onTouchEnd, capture);
      host.removeEventListener('touchcancel', onTouchEnd, capture);
      host.removeEventListener('contextmenu', onContextMenu, capture);
      host.removeEventListener('mousedown', onCompatibilityMouse, capture);
      host.removeEventListener('mouseup', onCompatibilityMouse, capture);
      host.removeEventListener('click', onCompatibilityMouse, capture);
      window.removeEventListener('blur', onWindowBlur);
      host.ownerDocument.removeEventListener('visibilitychange', onVisibilityChange);
      optionsRef.current.onContextMenuChange?.(false);
      finishSelection();
      releaseTerminalFocusBlock();
    };
  }, [
    enabled,
    gestureOwnerRef,
    hostRef,
    terminalRef
  ]);
}

export async function copyTerminalText(text: string): Promise<boolean> {
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    try {
      document.body.appendChild(textarea);
      textarea.select();
      return document.execCommand('copy');
    } catch {
      // Sandboxed webviews and browsers without the legacy command can throw
      // instead of returning false. Keep the contextual action harmless.
      return false;
    } finally {
      textarea.remove();
    }
  }
}

/**
 * Reads the clipboard only from an explicit contextual Paste click. Browsers
 * intentionally restrict this API, especially in embedded mobile browsers;
 * callers receive null instead of an exception so they can show a concise
 * permission/unavailability message without disrupting the terminal.
 */
export async function readTerminalClipboardText(): Promise<string | null> {
  if (!navigator.clipboard?.readText) return null;
  try {
    return await navigator.clipboard.readText();
  } catch {
    return null;
  }
}

type TerminalSelectionActionsProps = {
  visible: boolean;
  onCopy: () => void;
  onPaste: () => void;
  copyDisabled: boolean;
  pasteDisabled: boolean;
  selectionMode?: boolean;
  onToggleSelectionMode?: () => void;
  onUpload?: () => void;
  uploadDisabled?: boolean;
  uploadLabel?: string;
};

/**
 * The long-press selection menu mirrors a terminal's contextual actions:
 * copying the selected transcript and pasting the system clipboard. Paste is
 * deliberately exposed alongside a selection because a long press is the
 * trusted mobile gesture that opens this menu.
 */
export function TerminalSelectionActions({
  visible,
  onCopy,
  onPaste,
  copyDisabled,
  pasteDisabled,
  selectionMode = false,
  onToggleSelectionMode,
  onUpload,
  uploadDisabled = false,
  uploadLabel = 'Upload'
}: TerminalSelectionActionsProps) {
  if (!visible) return null;
  return (
    <div className="terminal-panel__selection-actions" role="group" aria-label="Terminal selection actions">
      {onToggleSelectionMode && (
        <button
          type="button"
          className="ghost terminal-panel__selection-select"
          aria-label="Select terminal text"
          title="Select terminal text with the next drag"
          aria-pressed={selectionMode}
          onClick={onToggleSelectionMode}
        >
          {selectionMode ? 'Selecting' : 'Select'}
        </button>
      )}
      <button
        type="button"
        className="ghost terminal-panel__selection-copy"
        aria-label="Copy selected terminal text"
        title="Copy selected terminal text"
        disabled={copyDisabled}
        onClick={onCopy}
      >
        Copy
      </button>
      <button
        type="button"
        className="ghost terminal-panel__selection-paste"
        aria-label="Paste terminal clipboard"
        title="Paste terminal clipboard"
        disabled={pasteDisabled}
        onClick={onPaste}
      >
        Paste
      </button>
      {onUpload && (
        <button
          type="button"
          className="ghost terminal-panel__selection-upload"
          aria-label="Upload a file to the terminal"
          title="Upload into the active pane directory and paste its path"
          disabled={uploadDisabled}
          onClick={onUpload}
        >
          {uploadLabel}
        </button>
      )}
    </div>
  );
}
